import { useRef } from 'react';
import { MESSAGE_RETENTION_DAYS, type Avatar } from '@sokdaq/protocol';
import { HAIRS, SKINS, TOPS, avatarFor } from '../avatar.js';
import { useDismiss } from '../hooks/useDismiss.js';
import { playSound } from '../sound.js';
import { useChatStore } from '../store/chat.js';
import { useSettingsStore, type NotifyMode } from '../store/settings.js';
import { AvatarPreview } from './AvatarPreview.js';
import { OwnerControls } from './OwnerControls.js';

const NOTIFY_LABELS: Record<NotifyMode, string> = {
  all: '모두',
  whisper: '속닥만',
  off: '끄기',
};

/**
 * 설정 패널.
 *
 * 항목이 적어서 별도 화면을 만들지 않고 머리글에서 여는 팝오버로 둔다.
 * 설정을 보려고 대화를 떠나야 하면 아무도 열지 않는다.
 */
export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const notify = useSettingsStore((s) => s.notify);
  const sound = useSettingsStore((s) => s.sound);
  const setNotify = useSettingsStore((s) => s.setNotify);
  const setSound = useSettingsStore((s) => s.setSound);
  const setAvatar = useSettingsStore((s) => s.setAvatar);

  const you = useChatStore((s) => s.you);
  const updateProfile = useChatStore((s) => s.updateProfile);

  const panelRef = useRef<HTMLDivElement>(null);

  // Esc로 닫고, 바깥을 누르면 닫는다. 위장 중에는 머리글이 없어서
  // 이 패널도 열릴 수 없으므로 보스키와 겹칠 일은 없다.
  useDismiss(panelRef, true, onClose, { toggleSelector: '[data-settings-toggle]' });

  /** 아바타는 바꾸는 즉시 서버에 알린다. '적용' 버튼을 따로 두지 않는다. */
  function applyAvatar(patch: Partial<Avatar>): void {
    const base = you?.avatar ?? avatarFor('');
    const next: Avatar = { ...base, ...patch };
    setAvatar(next);
    updateProfile({ avatar: next });
  }

  const current = you?.avatar ?? null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="설정"
      className="absolute right-2 top-full z-20 mt-1 max-h-[70vh] w-64 overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-900 p-3 shadow-xl"
    >
      <Section title="알림">
        <div className="flex gap-1">
          {(Object.keys(NOTIFY_LABELS) as NotifyMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setNotify(mode)}
              aria-pressed={notify === mode}
              className={
                notify === mode
                  ? 'flex-1 rounded bg-neutral-100 px-2 py-1 text-xs text-neutral-900'
                  : 'flex-1 rounded px-2 py-1 text-xs text-neutral-400 hover:bg-neutral-800'
              }
            >
              {NOTIFY_LABELS[mode]}
            </button>
          ))}
        </div>
        {notify === 'whisper' && (
          <p className="mt-1 text-[10px] text-neutral-500">
            나를 향한 속닥만 알립니다. 공개 대화는 조용히 쌓입니다.
          </p>
        )}
      </Section>

      <Section title="효과음">
        <button
          type="button"
          onClick={() => {
            const next = !sound;
            setSound(next);
            // 켠 직후 한 번 들려준다. 볼륨이 적당한지 확인할 데가 없다.
            if (next) playSound('react');
          }}
          aria-pressed={sound}
          className="w-full rounded px-2 py-1 text-left text-xs text-neutral-300 hover:bg-neutral-800"
        >
          <span className={sound ? 'text-emerald-400' : 'text-neutral-600'}>●</span>{' '}
          {sound ? '켜짐' : '꺼짐'}
        </button>
      </Section>

      <Section title="아바타">
        {/*
          아래 items-start가 없으면 미리보기 상자가 색 격자 높이만큼 늘어난다.
          터치에서 조각이 커지면서 격자가 두 배로 높아지기 때문에, 폰에서만
          아바타 뒤로 빈 상자가 길게 따라붙는다.
        */}
        {current === null ? (
          <p className="text-[10px] text-neutral-500">입장하면 바꿀 수 있습니다.</p>
        ) : (
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded bg-neutral-800 p-1">
              <AvatarPreview avatar={current} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Swatches label="피부" colors={SKINS} selected={current.skin} onPick={(skin) => applyAvatar({ skin })} />
              <Swatches label="머리" colors={HAIRS} selected={current.hair} onPick={(hair) => applyAvatar({ hair })} />
              <Swatches label="상의" colors={TOPS} selected={current.top} onPick={(top) => applyAvatar({ top })} />
            </div>
          </div>
        )}
      </Section>

      {/* 방장이 아니면 아무것도 그리지 않는다. */}
      <OwnerControls />

      {/*
        입장 화면의 한 줄로는 부족한 사람을 위한 자리.

        들어온 뒤에 "그런데 이거 누가 보나?"가 떠오르면 찾을 데가 있어야 한다.
        맨 아래에 두는 건 숨기려는 게 아니라, 매번 설정을 열 때 눈에 걸리는
        자리에 두면 곧 배경처럼 읽히지 않게 되기 때문이다.
      */}
      <Section title="이 방의 비밀">
        <p className="text-[10px] leading-relaxed text-neutral-400">
          종단간 암호화를 하지 않습니다. 오가는 동안은 TLS로, 저장될 때는
          Cloudflare가 암호화하지만,{' '}
          <span className="text-neutral-300">이 서버를 띄운 사람은 대화를 읽을 수 있습니다.</span>{' '}
          근접 속닥도 서버가 거리를 재기 때문에 본문이 서버를 지납니다.
        </p>
        {/*
          위가 나쁜 소식이니 실제로 있는 보호도 함께 적는다. 둘 다 사실이고,
          한쪽만 적으면 정확하지 않다.

          날짜는 상수에서 읽는다. 손으로 적어 두면 보존 기간을 바꿀 때 문구가
          남아서, 화면이 사실과 다른 말을 하게 된다 — 프라이버시 고지에서
          그건 그냥 거짓말이다.
        */}
        <p className="mt-1.5 text-[10px] leading-relaxed text-neutral-400">
          대신 저장된 대화는 <span className="text-neutral-300">{MESSAGE_RETENTION_DAYS}일 뒤 지워집니다.</span>{' '}
          속닥은 처음부터 저장하지 않습니다 — 그 자리에 있던 사람에게만 보이고 사라집니다.
        </p>
        <p className="mt-1.5 text-[10px] leading-relaxed text-neutral-500">
          초대코드를 아는 사람은 누구나 들어옵니다. 코드가 샜다면 방장이 설정에서
          새 코드로 바꿀 수 있습니다.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-3 last:mb-0">
      <h2 className="mb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Swatches({
  label,
  colors,
  selected,
  onPick,
}: {
  label: string;
  colors: readonly string[];
  selected: string;
  onPick: (color: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-6 shrink-0 text-[10px] text-neutral-500">{label}</span>
      <div className="flex flex-wrap gap-1">
        {colors.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onPick(color)}
            aria-label={`${label} ${color}`}
            aria-pressed={selected === color}
            style={{ backgroundColor: color }}
            className={`swatch rounded-sm ${
              selected === color ? 'ring-2 ring-neutral-100' : 'ring-1 ring-neutral-700'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
